import {keys} from 'lodash';
import {StringUtils, Utils, Logger, ApiClientCommunication} from 'neo-common';
import {LaunchClientOptions} from './LaunchClientOptions';
import {LaunchClientRequest} from './LaunchClientRequest';
import {LaunchClientResponse, LaunchAction} from './LaunchClientResponse';

const serviceUrlPath = 'LaunchService';
const gameHtmlUrl = 'index.html';

const defaultError: ApiClientCommunication.Types.IErrorResponse = {
    errorId: '',
    code: 0,
    message: 'Unable to launch game!'
};

/**
 * @class LaunchClient - class for handling launching game on local environment.
 */
export class LaunchClient extends ApiClientCommunication.ApiClient {
    private _initialized = false;
    private _launchServiceParams: Object;
    private _gameUrl: string;
    private _responseError: ApiClientCommunication.Types.ApiClientError;
    private _errorAsWarning: boolean;

    /**
     *
     * @param gameKey - key for game module
     */
    constructor(gameKey: string) {
        super(gameKey, new ApiClientCommunication.ApiClientOptions('dummy', null, false, 0));

        this.onHandleError = this.onHandleError.bind(this);
        this.onErrorResponse = this.onHandleError;
        this._errorAsWarning = false;
    }

    public get gameUrl(): string {
        return this._gameUrl;
    }

    public get errorAsWarning(): boolean {
        return this._errorAsWarning;
    }

    protected sendKeepAliveRequest(): Promise<ApiClientCommunication.ResponseBase> {
        throw new Error('Not supported!');
    }

    private onHandleError(error: ApiClientCommunication.Types.ApiClientError): void {
        this._responseError = Utils.isNullOrUndefined(error)
            ? new ApiClientCommunication.Types.ApiClientError(0, null, defaultError)
            : error;
    }

    private initialize(options?: LaunchClientOptions): Promise<ApiClientCommunication.Types.ApiClientError> {
        this._initialized = false;
        this._responseError = null;

        options = Utils.isNullOrUndefined(options) ? LaunchClientOptions.get() : options;

        if (Utils.isNullOrUndefined(options) || Utils.isNullOrUndefined(options.launchServiceParams) ||
            keys(options.launchServiceParams).length === 0) {
            this._responseError = new ApiClientCommunication.Types.ApiClientError(0, null, defaultError);
        } else {
            var apiClientOptions = new ApiClientCommunication.ApiClientOptions(options.gameServer, null, false, 0);
            this.setOptions(apiClientOptions);

            this._launchServiceParams = options.launchServiceParams;
            this._initialized = true;
        }

        if (!Utils.isNullOrUndefined(this._responseError)) {
            return Promise.resolve(this._responseError);
        }

        var self = this;
        var request = new LaunchClientRequest(this._launchServiceParams);
        return this.sendRequest<LaunchClientResponse>(request, 'GET', serviceUrlPath)
            .then(response => {
                if (!Utils.isNullOrUndefined(response)) {
                    var queryString = response.data;
                    var fragment = response.fragment;

                    // if launch action is not equal to 'Allow', set response Error
                    if (response.action !== LaunchAction.Allow) {
                        self._responseError = new ApiClientCommunication.Types.ApiClientError(0, null, {
                            code: 0,
                            errorId: '',
                            message: response.message || defaultError.message
                        });
                    }

                    if ((StringUtils.isNullOrEmpty(fragment) || Utils.isNullOrUndefined(queryString)) &&
                        Utils.isNullOrUndefined(self._responseError)) {
                        self._responseError = new ApiClientCommunication.Types.ApiClientError(0, null, defaultError);
                    }

                    self._errorAsWarning = response.action === LaunchAction.Warning;

                    // if there is NO error or launch action is 'Warning' (even with error) build game url
                    if (Utils.isNullOrUndefined(self._responseError) || self._errorAsWarning) {
                        var uri = URI();
                        self._gameUrl = uri.filename(gameHtmlUrl).query(queryString).fragment(fragment).toString();
                        self._initialized = true;
                    }
                }

                return null;
            })
            .then(() => {
                return self._responseError;
            });
    }

    /**
     * @method redirectToGame - redirects from current page to game client page according to data from launch service from server.
     */
    public redirectToGame(): Promise<ApiClientCommunication.Types.ApiClientError> {
        var self = this;
        return this.initialize()
            .then(error => {
                if (Utils.isNullOrUndefined(error)) {
                    if (!StringUtils.isNullOrEmpty(self._gameUrl)) {
                        if (DEBUG) {
                            Logger.debugGroup('LaunchClient', 'redirectToGame, redirects to url: ' + self._gameUrl);
                        }

                        location.href = self._gameUrl;
                    }
                }

                return error;
            });
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Communication/Launch/LaunchClient.ts
 **/